/**
 * Cache Manager - Provides a unified interface for managing cached data with TTL support
 * Uses sessionStorage for temporary session-based caching
 */

export const TTL_PRESETS = {
  SHORT: 60 * 1000,         // 1 minute
  MEDIUM: 5 * 60 * 1000,    // 5 minutes
  LONG: 15 * 60 * 1000,     // 15 minutes
  VERY_LONG: 60 * 60 * 1000 // 1 hour
} as const;

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
}

class CacheManager {
  private storage: Storage;

  constructor() {
    // Use sessionStorage for temporary caching (cleared when tab is closed)
    this.storage = window.sessionStorage;
  }

  /**
   * Set a value in cache with optional TTL
   */
  set<T>(key: string, value: T, options?: CacheOptions): void {
    try {
      const entry: CacheEntry<T> = {
        data: value,
        timestamp: Date.now(),
        ttl: options?.ttl || TTL_PRESETS.MEDIUM // Default to 5 minutes
      };

      this.storage.setItem(key, JSON.stringify(entry));
    } catch (error) {
      console.warn(`Failed to cache data for key "${key}":`, error);
    }
  }

  /**
   * Get a value from cache if it exists and hasn't expired
   * Returns null if the key doesn't exist or has expired
   */
  get<T>(key: string): T | null {
    try {
      const item = this.storage.getItem(key);

      if (!item) {
        return null;
      }

      const entry: CacheEntry<T> = JSON.parse(item);
      const now = Date.now();
      const age = now - entry.timestamp;

      // Check if entry has expired
      if (age > entry.ttl) {
        // Remove expired entry
        this.remove(key);
        return null;
      }

      return entry.data;
    } catch (error) {
      console.warn(`Failed to retrieve cached data for key "${key}":`, error);
      // Remove corrupted entry
      this.remove(key);
      return null;
    }
  }

  /**
   * Remove a specific key from cache
   */
  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch (error) {
      console.warn(`Failed to remove cached data for key "${key}":`, error);
    }
  }

  /**
   * Clear all cached data
   */
  clear(): void {
    try {
      this.storage.clear();
    } catch (error) {
      console.warn('Failed to clear cache:', error);
    }
  }

  /**
   * Check if a key exists and is not expired
   */
  has(key: string): boolean {
    return this.get(key) !== null;
  }

  /**
   * Get all cache keys
   */
  keys(): string[] {
    try {
      const keys: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key) {
          keys.push(key);
        }
      }
      return keys;
    } catch (error) {
      console.warn('Failed to get cache keys:', error);
      return [];
    }
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    try {
      const keys = this.keys();
      keys.forEach(key => {
        // This will automatically remove expired entries
        this.get(key);
      });
    } catch (error) {
      console.warn('Failed to cleanup cache:', error);
    }
  }
}

// Export a singleton instance
export const cacheManager = new CacheManager();

// Optional: Run cleanup on page load to remove stale entries
if (typeof window !== 'undefined') {
  window.addEventListener('load', () => {
    cacheManager.cleanup();
  });
}

export default cacheManager;
